import attachmentIcon from "assets/svgs/ic_attachment.svg";
import React, { useEffect, useRef, useState } from "react";
import { AttachmentType } from "app/containers/Features/types";
import CustomPopover from "./CustomPopover";

interface AttachmentsProps {
  count: number;
  attachments: AttachmentType[];
}

const HOVER_CLOSE_DELAY = 120;

const Attachments: React.FC<AttachmentsProps> = ({ count, attachments }) => {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleHoverStart = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  const handleHoverEnd = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, HOVER_CLOSE_DELAY);
  };

  useEffect(() => () => clearCloseTimeout(), []);

  return (
    <React.Fragment>
      <CustomPopover
        show={open}
        onClose={(val) => setOpen(val)}
        trigger={
          <div
            className="flex flex-row items-center rounded px-1 py-[1px] hover:bg-gray-200"
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          >
            <div className="min-w-[16px]">
              <img
                src={attachmentIcon}
                alt="task-icon"
                className="size-[16px]"
              />
            </div>
            <div className="text-xs leading-[18px] text-secondary font-normal mt-[2px] ml-[2px]">
              {count}
            </div>
          </div>
        }
        content={
          <div
            className="flex flex-col gap-4"
            onMouseEnter={handleHoverStart}
            onMouseLeave={handleHoverEnd}
          >
            <div className="sticky top-0 bg-white pt-4">
              <p className="text-md font-medium text-black">
                {count} attachment{count > 1 ? "s:" : ":"}
              </p>
            </div>
            {attachments.length === 0 ? (
              <p className="text-xs text-secondary">No preview available</p>
            ) : (
              attachments.map((attachment) => {
                const url = attachment?.url;
                if (!url) return null;

                const isImage =
                  attachment?.mimeType?.startsWith("image/") ||
                  /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);
                const isDocFile =
                  /\.(doc|docx|ppt|pptx|xls|xlsx|pdf|txt)(\?|$)/i.test(url) ||
                  attachment?.mimeType?.startsWith("application/");

                return (
                  <a
                    key={attachment?.fileId || url}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded border border-gray-100 p-1 hover:bg-gray-400"
                  >
                    {isImage ? (
                      <img
                        src={url}
                        alt="attachment preview"
                        className="w-full h-[140px] rounded object-cover"
                      />
                    ) : (
                      <div className="w-full h-[140px] rounded bg-black text-white text-xs font-semibold flex items-center justify-center">
                        {isDocFile ? "DOC" : "FILE"}
                      </div>
                    )}
                  </a>
                );
              })
            )}
          </div>
        }
        className="max-h-[300px] !pt-0"
      />
    </React.Fragment>
  );
};

export default Attachments;
